/**
 * useInfiniteSchoolQuery Hook
 *
 * A reusable hook for infinite scrolling school data queries.
 * Uses TanStack Query's useInfiniteQuery for efficient data fetching,
 * caching, and pagination management.
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { mockFetchSchools } from "@mocks/schoolDetails";
import type {
  SchoolData,
  SchoolSearchParams,
  PaginatedSchoolResponse,
} from "@mocks/schoolDetails";

export interface UseInfiniteSchoolQueryOptions {
  /** Search term to filter schools */
  searchTerm: string;
  /** Whether the query should be enabled */
  enabled: boolean;
  /** Number of items per page (default: 20) */
  limit?: number;
}

export interface UseInfiniteSchoolQueryResult {
  /** Flattened array of all school data across pages */
  schools: SchoolData[];
  /** Whether the initial query is loading */
  isLoading: boolean;
  /** Whether more data is being fetched */
  isFetchingNextPage: boolean;
  /** Whether there are more pages to load */
  hasNextPage: boolean;
  /** Function to load the next page */
  fetchNextPage: () => void;
  /** Whether there was an error */
  isError: boolean;
  /** Error object if there was an error */
  error: Error | null;
  /** Refetch the query */
  refetch: () => void;
  /** Whether the query is currently fetching */
  isFetching: boolean;
  /** Total number of schools across all pages */
  totalCount: number;
  /** Whether the search has no results */
  isEmpty: boolean;
}

/**
 * Hook for infinite scrolling school queries
 */
export const useInfiniteSchoolQuery = ({
  searchTerm,
  enabled,
  limit = 10,
}: UseInfiniteSchoolQueryOptions): UseInfiniteSchoolQueryResult => {
  // Create query parameters
  const queryParams: SchoolSearchParams = useMemo(
    () => ({
      search: searchTerm.trim(),
      limit,
    }),
    [searchTerm, limit]
  );

  // Use infinite query for paginated data
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ["schools", "infinite", queryParams],
    queryFn: ({ pageParam = 0 }: { pageParam: number }) =>
      mockFetchSchools({
        ...queryParams,
        cursor: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: PaginatedSchoolResponse) => {
      return lastPage.hasMore ? lastPage.cursor + 1 : undefined;
    },
    enabled: enabled && (queryParams.search?.length || 0) > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // Flatten all pages into a single array
  const schools = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data?.pages]);

  // Calculate total count from the first page response
  const totalCount = useMemo(() => {
    return data?.pages?.[0]?.total ?? 0;
  }, [data?.pages]);

  // Check if search has no results
  const isEmpty = useMemo(() => {
    return (
      !isLoading &&
      schools.length === 0 &&
      (queryParams.search?.length || 0) > 0
    );
  }, [isLoading, schools.length, queryParams.search]);

  return {
    schools,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    isError,
    error: error as Error | null,
    refetch,
    isFetching,
    totalCount,
    isEmpty,
  };
};

/**
 * Hook for debounced school search
 * Automatically debounces the search term to avoid excessive API calls
 */
export const useDebouncedSchoolQuery = ({
  searchTerm,
  enabled,
  limit = 10,
}: UseInfiniteSchoolQueryOptions): UseInfiniteSchoolQueryResult => {
  // Simple debounce implementation using useMemo and setTimeout
  const debouncedSearchTerm = useMemo(() => {
    if (!searchTerm.trim()) return "";

    // For initial implementation, we'll use the search term directly
    // In a production app, you might want to use a more sophisticated debounce
    return searchTerm.trim();
  }, [searchTerm]);

  return useInfiniteSchoolQuery({
    searchTerm: debouncedSearchTerm,
    enabled: enabled && debouncedSearchTerm.length >= 3,
    limit,
  });
};
